/** @module csi/action/FormData */
define([
	"dojo/_base/declare",
	"csi",
	"csi/_base/storage",
	"csi/_base/underscore",
	"csi/xml",
], function(declare, csi, storage, _, xml) {

	return declare(null, {
		// summary: hold changes made to forms used during actions

		// Boolean: indicates that table results should be pre-selected
		// NOTE: this will only fully work if page size is set to All, otherwise will only get first page pre-selected;
		// this would be much easier if we do track selections by url rather than node
		bPreselectAll: false,

		/** 
		 * @property {boolean} useParentNameInSelectionOverview - indicates name of parent node should be combined with name 
		 * of selected node in selection overview panel; this is a temporary fix until aggregate stores are fully deployed
		 * as this is something the aggregate store can handle
		 */
		useParentNameInSelectionOverview: false,

		/** @property {Node} m_parentNode - parent node of the current instance */
		m_parentNode: null,

		// Controller|FormData: source has interface with the following methods
		//		getCache()
		//		getNode()
		//		getParent()
		//		getAction()
		//			returns the object with the following (optional) parameters:
		//				aggregateClassIds
		// 				aggregateClass
		//				aggregateId
		//				bReverse
		//	Alternatively, you can assign properties to FormData with the following keys:
		//		m_cache,
		//		m_node,
		//		m_parentNode,
		//		m_action
		m_source: null,

		// Node: node being edited in form - if false, then default to source node -- could be null if creating new node
		m_node: false,

		// Field[]: fields in form
		m_fields: null,

		// Function: extension point for checking if node can be de-selected
		//		Boolean function(/*Context*/ctx, /*FormData*/fd)
		m_fAggregateTest: null,

		// Function: extension point for special checking if node is by default (or already) selected
		//		Boolean function(/*Context*/ctx, /*FormData*/fd)
		// NOTE: this will not work with inline editing (use in conjunction with Aggregate DialogOnly)
		m_fAggregateSelected: null,

		// Boolean: indicate if checkboxes should be added to allow selections
		m_bAllowSelections: true,

		// Boolean: indicate if toolbar selection actions should be shown
		m_bAllowSelectionActions: true,

		// Boolean: indicate if popup view should allow editing
		m_bEditable: false,

		// String: determine link target frame for createBrowseAnchor() function
		linkTarget: null,

		// Boolean: indicate if existing selections should be disabled
		m_bOmitSelections: false,

		// Boolean: indicate if one node must be selected
		m_bRequireOne: false,

		// Boolean: indicate if only one node can be selected
		m_bSelectOne: false,

		// Number: tracks order of changes
		m_nCtr: 0,

		// Object: map of field control names to initial values
		m_initialValues: null,

		// Object: existing aggregated nodes
		//_mapAggregated: null,

		// Object: changes to selections
		//_mapSelections: null,

		// Object: map of values for persisted fields
		//_persistedValues: undefined,

		/** @property {FormDataSelectFunction[]} selectListeners - listeners for when selections occur */
		//_selectListeners: null,

		constructor: function(source) {
			this.m_source = source;
			this._mapAggregated = {};
			this._mapSelections = {};
		},

		/**
		 * Add listener for select events
		 * @param {FormDataSelectFunction} f
		*/
		addSelectListener(f) {
			this._selectListeners ||= [];
			this._selectListeners.push(f);
		},

		getSource: function() {
			return this.m_source;
		},

		getCache: function() {
			return this.m_source.getCache();
		},

		getNode: function() {
			return (this.m_node === null) ? null : (this.m_node || this.m_source.getNode());
		},

		getKeyContainer: function() {
			return this.m_source?.keyContainer;
		},

		getParent: function() {
			return this.m_parentNode || this.m_source.getParent();
		},

		getAction: function() {
			return this.m_action || this.m_source.getAction();
		},

		setAction: function(action) {
			this.m_action = action;
		},

		setNode: function(node) {
			this.m_node = node;
		},

		/**
		 * set parent
		 * @param {Node} node 
		 */
		setParent: function(node) {
			return this.m_parentNode = node;
		},

		/**
		 * Determine if any nodes were selected prior to this action
		 * @return {boolean}
		 */
		hasAggregations() {
			return !_.isEmpty(this._mapAggregated);
		},

		/**
		 * Determine if any nodes were (un)selected during this action
		 * @return {boolean}
		 */
		hasSelections: function() {
			return !_.isEmpty(this._mapSelections);
		},

		/**
		 * Return true if given url is in selection map
		 * @param {string} url
		 * @return {boolean}
		 */
		isSelected: function(url) {
			const obj = this._mapSelections[url];
			return obj && obj.bChecked;
		},

		getValidationError: function() {
			// summary: for now only handles validation for selecting nodes
			var nodes = this.getSelectedNodes();

			if (this.m_bRequireOne && !nodes.length) {
				// if no new selections, then make sure all previous selections were not removed
				var n1 = _.size(this._mapAggregated);
				var n2 = this.getRemovedNodes().length;
				if (n1 <= n2) {
					return "_ErrorRequired";
				}
			}

			return null;
		},

		getSelectionUrl: function(node, bForce) {
			// summary: if using class to provide context for relationship, then make sure to use url of shared node (not context);
			// if aggregate action in reverse, then just use selected url as it is the one to which the shared node is added
			var action = this.getAction();
			var url;

			if (action.aggregateClass && (bForce || !action.bReverse)) {
				if (action.aggregateClassIds && (xml.nodeGetType(node) == action.aggregateClass)) {

					url = xml.nodeGetAttributeValue(node, action.aggregateClassIds[0]);
					if (!xml.isValidURL(url))
						url = null;
				}
			}

			return url || xml.nodeGetURL(node);
		},

		isSelection: function(/*Context*/ctx, /*Boolean*/bForce) {
			var url = this.getSelectionUrl(ctx.getNode());
			var obj = this._mapSelections[url];

			return obj ? obj.bChecked : this.isAggregated(ctx, bForce);
		},

		isAggregated: function(/*Context*/ctx, /*Boolean*/bForce, /*Boolean*/bHashOnly) {
			var action = this.getAction();
			var node = ctx.getNode();
			var url = this.getSelectionUrl(node);
			var amap = this._mapAggregated;

			if (amap[url]) return true;
			if (bHashOnly) return false;

			// if reverse aggregation, then need to test nodes for aggregation on demand - _mapAggregated not preset
			if (bForce && action.bReverse) {
				// this.getNode() will be populated only in Aggregate actions (action in table Actions column)
				// this.getParent() will be populated in AggregateFrom case (action on table toolbar)
				const targetNode = this.getNode() || this.getParent();
				const nodeAgg = this.getAggregateNode(node, targetNode, ctx.cache);
				if (nodeAgg) {
					amap[url] = node;

					// copy node from view cache to source cache
					this.getCache().cacheNode(nodeAgg);
					return true;
				}
			}

			// finally, call special function if present
			if (this.m_fAggregateSelected && this.m_fAggregateSelected(ctx, this)) {
				amap[url] = node;
				return true;
			}

			return false;
		},

		/**
		 * Return true if node is aggregated / pre-selected
		 * @param {string} url
		 * @return {boolean}
		 */
		isAggregatedUrl: function(url) {
			return !!this._mapAggregated[url];
		},

		changeSelection: function(/*Context*/ctx, /*Boolean*/bChecked, /*Boolean?*/bQuiet) {
			// summary: handle select action to keep track of all changes; if node is null, that means deselect all
			//	NOTE: "ctx" can be changed by this function!
			// bQuiet: function should not call onSelect if quiet - used in cases to sync FormData with location history
			var amap = this._mapAggregated;
			var smap = this._mapSelections;
			var node = ctx.getNode();
			var url = node ? this.getSelectionUrl(node) : null;
			var bNew = !!node;
			var bOld = false;
			var obj;

			// if only selecting one, need to clear previous selections/aggregations first
			if (this.m_bSelectOne && bChecked) {
				// remove previous selections from map
				_.each(_.keys(smap), function(url2) {
					obj = smap[url2];

					if (obj && obj.bChecked && (url != url2)) {
						delete smap[url2];
						this._onSelect(ctx, url2, false, bNew, bQuiet);
						bOld = true;
					}
				}, this);

				// deselect the currently selected aggregated nodes
				if (!this.m_bOmitSelections) {
					_.each(amap, function(node, url2) {
						if (!smap[url2]) {
							this.changeSelection(ctx.setNode(node), false, bQuiet);
							bOld = true;
						}
					}, this);
				}
			} else {
				// make sure we bookkeep with aggregated node rather than original node
				node = amap[url] || node;
			}

			if (node) {
				ctx.setNode(node);
				obj = smap[url];

				// ensure that selected node is in FormData cache
				this.getCache().cacheNode(node);

				if (obj && (obj.bChecked != bChecked)) {
					// if node was already selected and is now toggled, just remove from selection map
					delete smap[url];
					this._onSelect(ctx, url, bChecked, bOld, bQuiet);
				} else if (this.isSelection(ctx) != bChecked) {
					// if node selection is changed from initial aggregated nodes, then store change
					var obj = {
						nChange: this.m_nCtr++,
						bChecked: bChecked,
						node: node
					};

					smap[url] = obj;
					this._onSelect(ctx, url, bChecked, bOld, bQuiet);
				}
			}
		},

		/**
		 * Extension point for view selections
		 * @param {Context} ctx 
		 * @param {cnl} url 
		 * @param {boolean} bOn 
		 * @param {boolean} bReplaced - true if there was previously a selection (if bOn) or will be a new selection (if not bOn)
		 */
		onSelect(ctx, url, bOn, bReplaced) { },

		/**
		 * Notify any listeners that a selection has been made
		 * @param {Context} ctx 
		 * @param {cnl} url 
		 * @param {boolean} bOn 
		 * @param {boolean} bReplaced 
		 * @param {boolean} [bQuiet] 
		 */
		_onSelect(ctx, url, bOn, bReplaced, bQuiet) {
			if (!bQuiet) {
				// legacy method
				this.onSelect(ctx, url, bOn, bReplaced);

				// new way to notify listeners
				this._selectListeners?.forEach((f) => f(ctx, url, bOn, bReplaced));
			}
		},

		deselectAggregatedNodes: function(/*Context*/ctx) {
			// summary: deselect all previously aggregated nodes
			_.each(this._mapAggregated, function(node, url) {
				this.changeSelection(ctx.setNode(node), false);
			}, this);
		},

		resetAggregations: function() {
			// summary: reset all aggregated nodes
			this._mapAggregated = {};
		},

		resetSelections: function(/*Context?*/ctx, /*Boolean?*/bState) {
			// summary: reset selections - if context given, then reverse selections one by one so
			// onSelect extension point is called to trigger corresponding view changes; if state given,
			// then reset only selections that match the state
			var smap = this._mapSelections;

			if (ctx) {
				_.each(_.values(smap), function(obj) {
					if ((bState == null) || (bState == obj.bChecked)) {
						this.changeSelection(ctx.setNode(obj.node), !obj.bChecked);
					}
				}, this);
			} else if (bState != null) {
				_.each(_.values(smap), function(obj) {
					if (obj.bChecked == bState) {
						var url = this.getSelectionUrl(obj.node);
						delete smap[url];
					}
				}, this);
			} else {
				this._mapSelections = {};
			}
		},

		canSelectNode: function(/*Context*/ctx) {
			// summary: determine if node can be selected
			if (!this.m_bAllowSelections) return false;
			if (this.m_bOmitSelections && this.isAggregated(ctx, true)) return false;

			return !this.m_fAggregateTest || this.m_fAggregateTest(ctx, this);
		},

		getSelectedNode: function(/*Boolean?*/bAll) {
			// summary: get selected node if only one
			var nodes = this.getSelectedNodes(bAll);
			return (nodes.length == 1) ? nodes[0] : null;
		},

		getSelectedNodes: function(/*Boolean?*/bAll) {
			// summary: if bAll is true, include all selections including already aggregated nodes;
			// otherwise, only return newly selected nodes
			// NOTE: if bAll is used, nodes are not guaranteed to be sorted.
			var smap = this._mapSelections;

			// get new selections
			var nodes = csi.transform(csi.sort(_.values(smap), "nChange"), function(obj) {
				return obj.bChecked ? obj.node : null;
			}) || [];

			// add existing selections if asking for all
			if (bAll) {
				_.each(this._mapAggregated, function(node, url) {
					if (!smap[url]) {
						nodes.push(node);
					}
				});
			}

			return nodes;
		},

		getSelectedValue: function() {
			return _.map(this.getSelectedNodes(true), xml.nodeGetURL);
		},

		/**
		 * Return names of the selectedNodes
		 * @return {string[]}
		 */
		getSelectedNames: function() {
			return _.map(this.getSelectedNodes(true), xml.nodeGetName);
		},

		getRemovedNodes: function() {
			var a = csi.sort(_.values(this._mapSelections), "nChange");

			return csi.transform(a, function(obj) {
				return obj.bChecked ? null : obj.node;
			}) || [];
		},

		/**
		 * Return nodes of type AggregateClass referenced by AggregateId
		 * @return {string[]}
		 */
		getAggregatedNodes: function() {
			return _.values(this._mapAggregated);
		},

		/**
		 * Return URLs of nodes referenced by nodes of type AggregateClass
		 * @return {string[]}
		 */
		getAggregatedReferences: function() {
			return _.keys(this._mapAggregated);
		},

		getAggregateNode: function(nodeSource, nodeTarget, cache) {
			var action = this.getAction();
			var urlTarget = xml.nodeGetURL(nodeTarget);
			var value = xml.nodeGetAttributeValue(nodeSource, action.aggregateId);
			if (value) {
				if (action.aggregateClass) {
					var node = null;

					if (_.some(value, function(url) {
						node = cache.getNode(url);
						return (this.getSelectionUrl(node, true) == urlTarget);
					}, this)) {
						return node;
					}
				} else if (_.indexOf(value, urlTarget) >= 0) {
					return nodeTarget;
				}
			}

			return null;
		},

		/**
		 * Return list of selected urls; the only difference with getSelectedValue is if AggregateClass(Id) are specified;
		 * in such a case return the referenced url (from AggregateClassId) rather than the url of the aggregated node.
		 * The order of the returned value is not important.
		 * @return {cnl[]}
		 */
		getAggregateValue() {
			const {_mapAggregated, _mapSelections} = this;
			const ctx = this.getSource().getContext().copyContext();
			const set = new Set();

			for (const [key, value] of Object.entries(_mapAggregated)) {
				if (this.canSelectNode(ctx.setNodeURL(key))) {
					set.add(key);
				}
			}

			for (const [key, value] of Object.entries(_mapSelections)) {
				if (value.bChecked) {
					set.add(key);
				} else {
					set.delete(key);
				}
			}

			return Array.from(set);
		},

		addAggregatedNode: function(node) {
			this._mapAggregated[this.getSelectionUrl(node)] = node;
		},

		clearUsedFields: function(/*Field[]?*/fields) {
			// summary: clear used flag of fields
			_.each(fields || this.m_fields, function(f) {
				f.m_bUsed = false;
			});
		},

		setFields: function(/*Field[]*/fields) {
			// summary: handle case where same form data is used for multiple pages of wizard
			this.clearUsedFields(fields);
			this.m_fields = fields ? _.union(this.m_fields || [], fields) : null;
		},

		getFields: function() {
			return this.m_fields || [];
		},

		getField: function(controlName) {
			return csi.search(this.m_fields, function(f) {return f.getControlName() == controlName;});
		},

		getFieldById: function(fieldId) {
			return _.find(this.m_fields, function(f) {
				return (f.m_id == fieldId);
			});
		},

		getFieldValue: function(/*String|Field*/field, /*Boolean*/bUse) {
			let value;

			if (typeof field === "string") {
				field = this.getField(field);
			}

			if (field) {
				// mark as used so that it will not be published later
				if (bUse) {
					field.m_bUsed = true;
				}

				value = field.getValue();

				if (value == null) {
					value = this.getFieldInitialValue(field.getControlName());
				}
			}

			return value;
		},

		getFieldValueAsFile: function(/*String|Field*/field, /*Boolean*/bUse) {
			if (typeof field === "string") {
				field = this.getField(field);
			}

			if (field && field.m_control && field.m_control.files) {
				// mark as used so that it will not be published later
				if (bUse) {
					field.m_bUsed = true;
				}

				return field.m_control.files[0];
			}

			return null;
		},

		isFieldChanged: function(/*String|Field*/field) {
			if (typeof field === "string") {
				field = this.getField(field);
			}
			return field && field.isUpdated();
		},

		setFieldInitialValue: function(controlName, value) {
			if (!this.m_initialValues)
				this.m_initialValues = {};
			this.m_initialValues[controlName] = value;
		},

		getFieldInitialValue: function(controlName) {
			return this.m_initialValues ? this.m_initialValues[controlName] : null;
		},

		getViewPreferenceContext: function() {
			var source = this.getSource();
			var sourceNode = source ? source.getNode() : this.getNode();
			var parent = source ? source.getParent() : this.getParent();
			var action = source ? source.getAction() : this.getAction();
			var none = "null";
			var a = [
				action ? action.getId() : none,
				sourceNode ? sourceNode.$Type : none,
				parent ? parent.$Type : none
			];

			return a.join("|");
		},

		getPersistedFieldValue: function(/*String*/uniqueViewId, /*String*/fieldKey) {
			if (this._persistedValues === undefined) {
				var s = storage.get(this._getKeyForStorage(uniqueViewId));
				this._persistedValues = s ? s : {};
			}
			return this._persistedValues[fieldKey];
		},

		savePersistedFieldValues: function(/*String*/uniqueViewId, /*Field[]*/fields) {
			var obj;

			_.each(fields, function(field) {
				if (field.bPersist) {
					obj = obj || {};
					obj[field.getKey()] = field.getValue();
				}
			}, this);

			if (this._persistedValues = obj) {
				storage.set(this._getKeyForStorage(uniqueViewId), obj);
			}
		},

		_getKeyForStorage: function(key) {
			// summary: replace all non-alphanumeric characters with underscore
			return key.replace(/[^0-9a-zA-Z]/g, '_') + "_fields";
		},


		/*
		 * Return an object that maps form data field keys to values
		 * @returns {Object} map of keys to values
		 * */
		getFieldKeyValueMap: function() {
			var keyValueData = {};

			_.forEach(this.getFields(), function(field) {
				var fieldName = field.getKey();
				var fieldValue = field.getValue();

				keyValueData[fieldName] = fieldValue;
			});

			return keyValueData;
		}
	});
});
